import React from "react";
import { Row, Col, Card, Statistic, Table, Tag, Spin, Alert, Typography } from "antd";
import {
  ShoppingCartOutlined,
  FileTextOutlined,
  TruckOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useGetDashboardStatisticsQuery } from "../redux/api/dashboardApi";
import OrderTrendsChart from "../components/OrderTrendsChart.jsx";

const { Title } = Typography;

const DashboardPage = () => {
  const { data: statistics, isLoading, error } = useGetDashboardStatisticsQuery();

  // Table columns for recent activity
  const activityColumns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={type === "order" ? "blue" : "orange"}>
          {type === "order" ? "Order" : "Proposal"}
        </Tag>
      ),
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (name) => name || "N/A",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => `$${parseFloat(amount || 0).toFixed(2)}`,
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Alert
            title="Loading dashboard statistics..."
            type="info"
            showIcon
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Title level={2}>Dashboard</Title>
        <Alert
          title="Error loading dashboard"
          description={error?.data?.message || error?.error || "Unknown error"}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      <Title level={2} style={{ marginBottom: "24px" }}>
        Dashboard
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Orders"
              value={statistics?.active_orders || 0}
              prefix={<ShoppingCartOutlined />}
              styles={{ content: { color: "#1890ff" } }}
            />
            <div style={{ marginTop: "8px", color: "#8c8c8c", fontSize: "14px" }}>
              Orders currently being processed.
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Proposals Under Negotiation"
              value={statistics?.pending_proposals || 0}
              prefix={<FileTextOutlined />}
              styles={{ content: { color: "#faad14" } }}
            />
            <div style={{ marginTop: "8px", color: "#8c8c8c", fontSize: "14px" }}>
              Proposals being discussed with customers.
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Deliveries in Progress"
              value={statistics?.deliveries_in_progress || 0}
              prefix={<TruckOutlined />}
              styles={{ content: { color: "#13c2c2" } }}
            />
            <div style={{ marginTop: "8px", color: "#8c8c8c", fontSize: "14px" }}>
              Items currently being delivered.
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Unpaid Invoices"
              value={statistics?.unpaid_invoices || 0}
              prefix={<DollarOutlined />}
              styles={{ content: { color: "#f5222d" } }}
            />
            <div style={{ marginTop: "8px", color: "#8c8c8c", fontSize: "14px" }}>
              Invoices awaiting payment.
            </div>
          </Card>
        </Col>
      </Row>
      
      {/* Order Trends Chart */}
      <Card
        title="Order Trends"
        style={{ marginTop: "24px" }}
      >
        <div style={{ height: "300px" }}>
          <OrderTrendsChart orderTrends={statistics?.order_trends || []} />
        </div>
      </Card>

      {/* Recent Activity */}
      {statistics?.recent_activity && statistics.recent_activity.length > 0 && (
        <Card title="Recent Activity" style={{ marginTop: "24px" }}>
          <Table
            columns={activityColumns}
            dataSource={statistics.recent_activity.map((activity, index) => ({
              ...activity,
              key: index,
            }))}
            pagination={false}
            size="middle"
          />
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
